/* eslint-disable react-refresh/only-export-components */
// This file intentionally exports both the Provider component and context hooks

import { createContext, useContext, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useLanguage } from './LanguageContext';

interface WebsiteContent {
  content: Record<string, string>;
  images: Array<{ key: string; url: string; alt_text: string }>;
  theme: Record<string, string>;
  isLoading: boolean;
  error: Error | null;
  updateContent: (section: string, key: string, value: string) => Promise<void>;
  updateImage: (key: string, url: string, altText: string) => Promise<void>;
  getContent: (section: string, key: string, fallback?: string) => string;
  getImage: (key: string) => { url: string; alt_text: string } | undefined;
  getThemeColor: (key: string, fallback?: string) => string;
}

// Default theme colors (fallbacks if not set in CMS)
const DEFAULT_THEME: Record<string, string> = {
  primary_50: '#e6f4ea',
  primary_100: '#c8e6c9',
  primary_200: '#a5d6a7',
  primary_300: '#81c784',
  primary_400: '#66bb6a',
  primary_500: '#4caf50',
  primary_600: '#2e8b57',
  primary_700: '#388e3c',
  primary_800: '#2e7d32',
  primary_900: '#1b5e20',
  accent: '#d4af37',
  accent_dark: '#b8960c',
  logo_text_color: '#2E8B57',
  logo_text_color_light: '#FFFFFF',
  // Action colors for CTAs and highlights
  action_light: '#fbbf24',
  action: '#f59e0b',
  action_dark: '#ea580c',
  // Gamification/tier colors
  star_rating: '#facc15',
  tier_bronze: '#d97706',
  tier_gold: '#eab308',
  tier_super: '#f59e0b',
};

const WebsiteContentContext = createContext<WebsiteContent | null>(null);

export function WebsiteContentProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();

  // Fetch content with language parameter for translated content
  const {
    data: contentData,
    isLoading: contentLoading,
    error: contentError,
  } = useQuery({
    queryKey: ['website-content', language],
    queryFn: () => api.getWebsiteContent(undefined, language),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch images
  const {
    data: imagesData,
    isLoading: imagesLoading,
    error: imagesError,
  } = useQuery({
    queryKey: ['website-images'],
    queryFn: () => api.getWebsiteImages(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch theme colors
  const {
    data: themeData,
    isLoading: themeLoading,
  } = useQuery({
    queryKey: ['website-theme'],
    queryFn: () => api.getWebsiteTheme(),
    staleTime: 5 * 60 * 1000,
  });

  // Merge theme with defaults (memoized to prevent unnecessary re-renders)
  const theme = useMemo(
    () => ({ ...DEFAULT_THEME, ...(themeData?.theme || {}) }),
    [themeData?.theme]
  );

  // Apply theme colors to CSS variables
  useEffect(() => {
    const root = document.documentElement;

    // Apply all primary colors
    for (let i = 50; i <= 900; i += (i < 100 ? 50 : 100)) {
      const key = `primary_${i}`;
      const value = theme[key];
      if (value) {
        root.style.setProperty(`--color-primary-${i}`, value);
        root.style.setProperty(`--primary-${i}`, value);
      }
    }

    // Apply accent colors
    if (theme.accent) {
      root.style.setProperty('--color-accent', theme.accent);
      root.style.setProperty('--accent', theme.accent);
    }
    if (theme.accent_dark) {
      root.style.setProperty('--color-accent-dark', theme.accent_dark);
      root.style.setProperty('--accent-dark', theme.accent_dark);
    }

    // Apply logo colors
    if (theme.logo_text_color) {
      root.style.setProperty('--logo-text-color', theme.logo_text_color);
    }
    if (theme.logo_text_color_light) {
      root.style.setProperty('--logo-text-color-light', theme.logo_text_color_light);
    }

    // Apply action colors
    if (theme.action_light) {
      root.style.setProperty('--color-action-light', theme.action_light);
      root.style.setProperty('--action-light', theme.action_light);
    }
    if (theme.action) {
      root.style.setProperty('--color-action', theme.action);
      root.style.setProperty('--action', theme.action);
    }
    if (theme.action_dark) {
      root.style.setProperty('--color-action-dark', theme.action_dark);
      root.style.setProperty('--action-dark', theme.action_dark);
    }

    // Apply gamification/tier colors
    if (theme.star_rating) {
      root.style.setProperty('--color-star-rating', theme.star_rating);
    }
    if (theme.tier_bronze) {
      root.style.setProperty('--color-tier-bronze', theme.tier_bronze);
    }
    if (theme.tier_gold) {
      root.style.setProperty('--color-tier-gold', theme.tier_gold);
    }
    if (theme.tier_super) {
      root.style.setProperty('--color-tier-super', theme.tier_super);
    }
  }, [theme]);

  // Content update mutation
  const contentMutation = useMutation({
    mutationFn: (data: { section: string; key: string; value: string }) =>
      api.updateAdminWebsiteContentBulk({
        updates: [{ section: data.section, key: data.key, value: data.value }],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-content'] });
    },
  });

  // Image update mutation
  const imageMutation = useMutation({
    mutationFn: (data: { key: string; url: string; alt_text: string }) =>
      api.updateAdminWebsiteImage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-images'] });
    },
  });

  const updateContent = async (section: string, key: string, value: string) => {
    await contentMutation.mutateAsync({ section, key, value });
  };

  const updateImage = async (key: string, url: string, altText: string) => {
    await imageMutation.mutateAsync({ key, url, alt_text: altText });
  };

  // Get content with translation support
  // First tries to get a translation from the strings API, then falls back to CMS content
  const getContent = useCallback((section: string, key: string, fallback = ''): string => {
    const fullKey = `${section}.${key}`;
    
    // First try to get translated string (will return the key if not found)
    const translated = t(fullKey);
    if (translated !== fullKey) {
      return translated;
    }
    
    // Fall back to CMS content
    return contentData?.content?.[fullKey] ?? fallback;
  }, [t, contentData?.content]);

  const getImage = useCallback((key: string) => {
    return imagesData?.images?.find((img) => img.key === key);
  }, [imagesData?.images]);

  const getThemeColor = useCallback((key: string, fallback = ''): string => {
    return theme[key] ?? DEFAULT_THEME[key] ?? fallback;
  }, [theme]);

  const value: WebsiteContent = {
    content: contentData?.content ?? {},
    images: imagesData?.images ?? [],
    theme,
    isLoading: contentLoading || imagesLoading || themeLoading,
    error: (contentError || imagesError) as Error | null,
    updateContent,
    updateImage,
    getContent,
    getImage,
    getThemeColor,
  };

  return (
    <WebsiteContentContext.Provider value={value}>
      {children}
    </WebsiteContentContext.Provider>
  );
}

export function useWebsiteContent() {
  const context = useContext(WebsiteContentContext);
  if (!context) {
    throw new Error('useWebsiteContent must be used within WebsiteContentProvider');
  }
  return context;
}

// Hook that doesn't throw - for use outside provider
export function useWebsiteContentOptional() {
  return useContext(WebsiteContentContext);
}
